import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import CustomSwitch from "../../../../components/CustomSwitch";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import sccm_assign_role from "./images/MicrosoftSCCM-assign-user-role.png";

const MICROSOFT_SCCM_CODE_NAME = "microsoft_sccm";

export default function MicrosoftSCCMIntegration() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [adUsername, setAdUsername] = useState("");
  const [adUserPassword, setAdUserPassword] = useState("");
  const [adDomainName, setAdDomainName] = useState("");
  const [sccmSiteServerIp, setSccmSiteServerIp] = useState("");
  const [isOnPrem, setIsOnPrem] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    registerMicrosoftSCCMCloudIntegration,
    registerMicrosoftSCCMOnPremIntegration,
  } = useAPI();
  const [registerIsLoading, setRegisterIsLoading] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    MICROSOFT_SCCM_CODE_NAME
  );
  const guideMode = searchParams.has("viewonly");
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const handleOnPremChange = (event) => {
    event.stopPropagation();
    setIsOnPrem((prev) => !prev);
  };

  const onSubmit = async () => {
    if (!adUsername || !adUserPassword || !adDomainName || !sccmSiteServerIp) {
      return;
    }

    setRegisterIsLoading(true);
    try {
      if (isOnPrem) {
        await registerMicrosoftSCCMOnPremIntegration(
          adUsername,
          adUserPassword,
          adDomainName,
          sccmSiteServerIp
        );
      } else {
        await registerMicrosoftSCCMCloudIntegration(
          adUsername,
          adUserPassword,
          adDomainName,
          sccmSiteServerIp
        );
      }
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Microsoft SCCM registered successfully");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setRegisterIsLoading(false);
    }
  };

  return (
    <Box>
      <Guideline
        title={isIntegrated ? "" : "Microsoft SCCM integration instructions"}
        image={logos.MicrosoftSCCMLogo}
        iconBorderRadius={4}
        name={"Microsoft SCCM"}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Microsoft SCCM is registered!
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Create a user in Active Directory - no domain rights just a domain user.">
                  <Typography variant="body2">
                    Note - Create the user in the same domain as where the SCCM
                    Site Server is installed.
                  </Typography>
                  <Typography variant="body2">
                    The password should be set to never expire to avoid
                    disruption.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Assign SCCM role to the user">
                  <Typography variant="body2">
                    Using the SCCM console - Assign the role - “Read-only
                    Analyst” to the integration user. Make sure to select the
                    “All instances of the objects that are related to the
                    assigned security roles” option (to allow read access across
                    SCCM)
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: sccm_assign_role,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Verify the SCCM AdminService is enabled and accessible ">
                  <Typography variant="body2">
                    Use a web browser from your internal network to access the
                    following URL:
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                    <Box
                      component="li"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: 600 }}
                      >
                        https://{"{"}your_sccm_server_ip_address{"}"}
                        /adminservice/v1.0/$metadata
                      </Typography>
                      <CustomCopyButton
                        copyTitle={"SCCM AdminService URL"}
                        copyText="https://{your_sccm_server_ip_address}/adminservice/v1.0/$metadata"
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2">
                    When prompted with user and password - use the previously
                    created credentials to test the connection.
                    <br />A valid response will print XML data like:
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      padding: "12px",
                      marginTop: "8px",
                      overflowX: "auto",
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "12px",
                        whiteSpace: "pre-wrap",
                        margin: 0,
                        color: "#333",
                      }}
                    >
                      &lt;edmx:Edmx
                      xmlns:edmx="http://docs.oasis-open.org/odata/ns/edmx"
                      Version="4.0"&gt; &lt;edmx:DataServices&gt; &lt;Schema
                      xmlns="http://docs.oasis-open.org/odata/ns/edm"
                      Namespace="Microsoft.ConfigurationManager.ObjectLibrary.Models"&gt;
                      &lt;EntityType Name="Application"&gt; &lt;Key&gt;
                      &lt;PropertyRef Name="CIGUID" /&gt; &lt;/Key&gt;
                      &lt;/EntityType&gt; &lt;/Schema&gt;
                      &lt;/edmx:DataServices&gt; &lt;/edmx:Edmx&gt;
                    </Typography>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Enter your credentials">
                  <Box
                    width={700}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    <Box display="flex" alignItems="center">
                      <FormControlLabel
                        sx={{ margin: 0, gap: 2, alignItems: "center" }}
                        disabled={guideMode}
                        control={
                          <CustomSwitch
                            checked={isOnPrem}
                            onChange={handleOnPremChange}
                            disabled={guideMode}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ cursor: guideMode ? "default" : "pointer" }}
                          >
                            On-Prem Deployment
                          </Typography>
                        }
                        labelPlacement="start"
                      />
                    </Box>

                    <TextField
                      placeholder="SCCM Site Server (Management point role) IP"
                      fullWidth
                      type="text"
                      value={sccmSiteServerIp}
                      onChange={(e) => setSccmSiteServerIp(e.target.value)}
                      disabled={guideMode}
                      helperText="IP address of your SCCM Site Server with Management point role"
                    />
                    <TextField
                      placeholder="AD Domain Name (e.g., contoso.com)"
                      fullWidth
                      type="text"
                      value={adDomainName}
                      onChange={(e) => setAdDomainName(e.target.value)}
                      disabled={guideMode}
                    />
                    <TextField
                      placeholder="AD Username"
                      fullWidth
                      type="text"
                      value={adUsername}
                      onChange={(e) => setAdUsername(e.target.value)}
                      disabled={guideMode}
                    />
                    <TextField
                      placeholder="AD User Password"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      value={adUserPassword}
                      onChange={(e) => setAdUserPassword(e.target.value)}
                      disabled={guideMode}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword((prev) => !prev)}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <ContainedButton
                      onClick={onSubmit}
                      size="small"
                      color="primary"
                      disabled={
                        guideMode ||
                        !adUsername ||
                        !adUserPassword ||
                        !adDomainName ||
                        !sccmSiteServerIp
                      }
                      loading={registerIsLoading}
                    >
                      Connect
                    </ContainedButton>
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          )
        }
      />
    </Box>
  );
}
